import { backgroundOrange, orange } from '@/theme/colors'
import { PlaceSuggestion } from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { EventCategory } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import EventForm from '../AddEventModal/EventForm'

interface EditEventModalProps {
	isOpen: boolean
	onClose: () => void
	onSubmit: (
		title: string,
		description: string,
		category: EventCategory | undefined,
		location: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		} | undefined,
		links: string[],
		startTime?: string | null,
	) => void
	onDelete?: () => void
	event: {
		name: string
		description?: string | null
		category?: EventCategory | null
		location?: {
			fullText: string
			primaryText: string | null
			secondaryText: string | null
			googlePlaceId: string | null
			googleTypes: string[]
		} | null
		tripEventLinks?: Array<{ url: string }>
		startTime?: string | null
	}
}

const EditEventModal: React.FC<EditEventModalProps> = ({ isOpen, onClose, onSubmit, onDelete, event }) => {
	const [title, setTitle] = useState(event.name)
	const [description, setDescription] = useState(event.description || '')
	const [category, setCategory] = useState<EventCategory | undefined>(event.category || 'Other')
	const [locationPrimaryText, setLocationPrimaryText] = useState(event.location?.primaryText || '')
	const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(null)
	const [links, setLinks] = useState<string[]>(event.tripEventLinks?.map(link => link.url) || [])
	const [startTime, setStartTime] = useState<string>(event.startTime || '')

	// Update form state when event prop changes or modal opens
	useEffect(() => {
		if (isOpen) {
			setTitle(event.name || '')
			setDescription(event.description || '')
			setCategory(event.category || 'Other')
			setLocationPrimaryText(event.location?.primaryText || '')
			// If we have full location data, populate selectedPlace
			if (event.location?.googlePlaceId) {
				setSelectedPlace({
					placeId: event.location.googlePlaceId,
					primaryText: event.location.primaryText || '',
					secondaryText: event.location.secondaryText || '',
					fullText: event.location.fullText,
					types: event.location.googleTypes || [],
				})
			} else {
				setSelectedPlace(null)
			}
			setLinks(event.tripEventLinks?.map(link => link.url) || [])
			setStartTime(event.startTime || '')
		}
	}, [isOpen, event.name, event.description, event.category, event.location, event.tripEventLinks, event.startTime])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (title.trim()) {
			const location = selectedPlace ? {
				fullText: selectedPlace.fullText,
				primaryText: selectedPlace.primaryText,
				secondaryText: selectedPlace.secondaryText || undefined,
				googlePlaceId: selectedPlace.placeId,
				googleTypes: selectedPlace.types,
			} : locationPrimaryText.trim() ? {
				fullText: locationPrimaryText.trim(),
				primaryText: locationPrimaryText.trim(),
				googleTypes: [],
			} : undefined

			onSubmit(title.trim(), description.trim(), category, location, links, startTime || null)
			setTitle('')
			setDescription('')
			setCategory('Other')
			setLocationPrimaryText('')
			setSelectedPlace(null)
			setLinks([])
			setStartTime('')
			onClose()
		}
	}

	const handleCancel = () => {
		// Reset to original values
		setTitle(event.name)
		setDescription(event.description || '')
		setCategory(event.category || 'Other')
		setLocationPrimaryText(event.location?.primaryText || '')
		if (event.location?.googlePlaceId) {
			setSelectedPlace({
				placeId: event.location.googlePlaceId,
				primaryText: event.location.primaryText || '',
				secondaryText: event.location.secondaryText || '',
				fullText: event.location.fullText,
				types: event.location.googleTypes || [],
			})
		} else {
			setSelectedPlace(null)
		}
		setLinks(event.tripEventLinks?.map(link => link.url) || [])
		setStartTime(event.startTime || '')
		onClose()
	}

	const handleLocationSelect = (place: PlaceSuggestion) => {
		setSelectedPlace(place)

		// When location is set, attempt to prefill name as well
		// Get the old location name from selectedPlace or locationPrimaryText
		const oldLocationName = (selectedPlace?.primaryText ?? selectedPlace?.fullText ?? locationPrimaryText ?? '')
			.substring(0, 75)
			.trim()

		// If title is empty or matches the old location name, update it with the new location
		const trimmedTitle = title.trim()
		if (trimmedTitle === '' || trimmedTitle === oldLocationName) {
			// Max length on title is 75 (same as iOS)
			const newTitle = (place.primaryText ?? place.fullText)
				.substring(0, 75)
				.trim()
			setTitle(newTitle)
		}
	}

	const handleTitleChange = (newTitle: string) => {
		setTitle(newTitle)
	}

	const handleDescriptionChange = (newDescription: string) => {
		setDescription(newDescription)
	}

	const handleCategoryChange = (newCategory: EventCategory | undefined) => {
		setCategory(newCategory)
	}

	const handleStartTimeChange = (newStartTime: string) => {
		setStartTime(newStartTime)
	}

	const handleLocationPrimaryTextChange = (newLocationPrimaryText: string) => {
		setLocationPrimaryText(newLocationPrimaryText)
		setSelectedPlace(null)
	}

	const handleLinksChange = (newLinks: string[]) => {
		setLinks(newLinks)
	}

	const handleDelete = () => {
		if (onDelete) {
			onDelete()
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title="Edit Event"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			closeButtonColor={orange}
			onDelete={onDelete ? handleDelete : undefined}
		>
		<EventForm
			title={title}
			description={description}
			category={category}
			locationPrimaryText={locationPrimaryText}
			locationSecondaryText={selectedPlace?.secondaryText || undefined}
			links={links}
				startTime={startTime}
				onTitleChange={handleTitleChange}
				onDescriptionChange={handleDescriptionChange}
				onCategoryChange={handleCategoryChange}
				onLocationPrimaryTextChange={handleLocationPrimaryTextChange}
				onLocationSelect={handleLocationSelect}
				onLinksChange={handleLinksChange}
				onStartTimeChange={handleStartTimeChange}
				onSubmit={handleSubmit}
				onCancel={handleCancel}
				submitButtonText="Save Changes"
				submitDisabled={!title.trim()}
			/>
		</Modal>
	)
}

export default EditEventModal
